<template>
    <VJoyDialogTrigger :dialog="localeSelectionDialog">
        <LocaleSelectionButton :locale="currentLanguageCode" :loading="!isHydrated" />
    </VJoyDialogTrigger>
    <VJoyDialog id="localeSelectionDialog" ref="localeSelectionDialog" data-testid="locale-selection-dialog">
        <template #dialog-header>{{ $t('navbar.feat.locale-selection.dialog.title') }}</template>
        <template #dialog-subheader>{{ $t('navbar.feat.locale-selection.dialog.subtitle') }}</template>
        <template #dialog-body>
            <CountryAndLanguageSelection
                :country-label="$t('navbar.feat.locale-selection.dialog.country.select.label')"
                :current-locale="currentLocale"
                :language-label="$t('navbar.feat.locale-selection.dialog.language.select.label')"
                data-testid="country-and-language-selection"
                @update:country-code="handleCountryOrLanguageChange"
                @update:language-code="handleCountryOrLanguageChange"
            />
            <CurrencySelection
                v-if="isMultiLocaleHost"
                data-testid="currency-selection"
                class="joy-mt-6"
                :selected-currency="userCurrency"
                @update:currency="handleCurrencyUpdate"
            />
        </template>
        <template #dialog-confirm>
            <VJoyButton
                :label="$t('navbar.feat.locale-selection.dialog.cta.continue')"
                size="small"
                variant="main"
                data-testid="locale-selection-continue"
                :disabled="!isCtaEnabled"
                :loading="isLoading"
                @click="handleClickOnContinue"
            />
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {ref, useTemplateRef} from 'vue';
    import {type CountryAndLanguageSelectionUpdateEvent, type CurrencyCode, type Language} from '@navbar-unlogged-types';
    import {storeToRefs} from 'pinia';
    import {useAuth, useLocale, useTranslation, useHydrationState} from '#imports';
    import {pushVJoySnackbar, VJoyButton, VJoyDialog, VJoyDialogTrigger} from '@maltjoy/core-vue';
    import CountryAndLanguageSelection from './CountryAndLanguageSelection.vue';
    import CurrencySelection from './CurrencySelection.vue';
    import LocaleSelectionButton from './LocaleSelectionButton.vue';
    import {useLocalizationSelection} from '../composables/useLocalizationSelection';
    import {languageCodeFromLocale} from '../utils/localeSelection';

    const authenticationStore = useAuth();
    const {userCurrency} = storeToRefs(authenticationStore);
    const {isMultiLocaleHost} = useLocale();
    const {changeLanguageAndRedirect, getCurrentLocale} = useLocalizationSelection();
    const currentLocale = getCurrentLocale();
    const currentLanguageCode = languageCodeFromLocale(currentLocale).toUpperCase();
    const {t} = useTranslation();

    const {isHydrated} = useHydrationState();

    const localeSelectionDialog = useTemplateRef('localeSelectionDialog');
    const selectedLanguage = ref<Language | undefined>();
    const selectedCurrency = ref<CurrencyCode | undefined>();
    // Enable CTA on malt.com because selected values are just suggested.
    // Disabled on other hosts until the user changes a value.
    const isCtaEnabled = ref(!!isMultiLocaleHost.value);
    const isLoading = ref(false);

    function handleCountryOrLanguageChange(_: string, {maybeSelectedLanguage}: CountryAndLanguageSelectionUpdateEvent) {
        selectedLanguage.value = maybeSelectedLanguage;
        isCtaEnabled.value = true;
    }

    function handleCurrencyUpdate(currencyCode: CurrencyCode) {
        selectedCurrency.value = currencyCode;
        isCtaEnabled.value = true;
    }

    async function handleClickOnContinue() {
        isLoading.value = true;
        await persistCurrencyPreference();
        await persistLanguagePreference();
        isLoading.value = false;
        localeSelectionDialog.value?.hide();
    }

    async function persistCurrencyPreference() {
        if (!selectedCurrency.value) {
            return;
        }

        try {
            authenticationStore.updateUserCurrency(selectedCurrency.value);

            if (!selectedLanguage.value) {
                location.reload();
            }
        } catch {
            pushVJoySnackbar({
                props: {
                    message: t('generic.technical.error'),
                    level: 'error',
                },
            });
        }
    }

    async function persistLanguagePreference() {
        if (selectedLanguage.value) {
            await changeLanguageAndRedirect(selectedLanguage.value);
        }
    }

    defineExpose({
        dialog: localeSelectionDialog,
    });
</script>
